// Zodiac signs configuration for SEO (Vedic moon signs)
export const ZODIAC_SIGNS = [
    { key: 'aries', name: 'Aries' },
    { key: 'taurus', name: 'Taurus' },
    { key: 'gemini', name: 'Gemini' },
    { key: 'cancer', name: 'Cancer' },
    { key: 'leo', name: 'Leo' },
    { key: 'virgo', name: 'Virgo' },
    { key: 'libra', name: 'Libra' },
    { key: 'scorpio', name: 'Scorpio' },
    { key: 'sagittarius', name: 'Sagittarius' },
    { key: 'capricorn', name: 'Capricorn' },
    { key: 'aquarius', name: 'Aquarius' },
    { key: 'pisces', name: 'Pisces' },
];

// Horoscope types configuration (matching API: cycle 1=weekly, 2=monthly, 3=yearly)
export const HOROSCOPE_TYPES = {
    weekly: { name: 'Weekly', changefreq: 'weekly' },
    monthly: { name: 'Monthly', changefreq: 'monthly' },
    yearly: { name: 'Yearly', changefreq: 'yearly' },
};

// Get horoscope SEO title (50-60 characters)
export const getHoroscopeSEOTitle = (signName, horoscopeType) => {
    const typeConfig = HOROSCOPE_TYPES[horoscopeType];
    const typeName = typeConfig ? typeConfig.name : horoscopeType;
    return `${signName} ${typeName} Horoscope`;
};

// Get horoscope SEO description (150-160 characters)
export const getHoroscopeSEODescription = (signName, horoscopeType) => {
    const typeConfig = HOROSCOPE_TYPES[horoscopeType];
    const typeName = typeConfig ? typeConfig.name.toLowerCase() : horoscopeType;
    return `Read your ${typeName} ${signName} horoscope. Get accurate Vedic astrology predictions and guidance from Prakash Astrologer.`;
};

// Get horoscope JSON-LD structured data
export const getHoroscopeJsonLd = (signName, horoscopeType, url) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: getHoroscopeSEOTitle(signName, horoscopeType),
    description: getHoroscopeSEODescription(signName, horoscopeType),
    author: {
        '@type': 'Person',
        name: 'Prakash Astrologer',
    },
    publisher: {
        '@type': 'Organization',
        name: 'Prakash Astrologer',
        logo: {
            '@type': 'ImageObject',
            url: 'https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg',
        },
    },
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
    },
});

// Static page SEO configurations
export const PAGE_SEO_CONFIG = {
    home: {
        title: 'Vedic Astrology & Horoscope Readings',
        description: 'Get personalised Vedic astrology readings, weekly and monthly horoscopes, consultations, and gemstone recommendations from Prakash Astrologer.',
        url: '/',
    },
    login: {
        title: 'Sign In',
        description: 'Sign in to your Prakash Astrologer account to access your personalised horoscopes and consultations.',
        url: '/',
    },
    signup: {
        title: 'Create Account',
        description: 'Sign up for Prakash Astrologer to access personalised horoscopes, save your birth chart, and book astrological consultations.',
        url: '/signup',
    },
    forgotPassword: {
        title: 'Forgot Password',
        description: 'Reset your Prakash Astrologer account password. Enter your email to receive password reset instructions.',
        url: '/forgot-password',
        noindex: true,
    },
    dashboard: {
        title: 'My Horoscopes Dashboard',
        description: 'View your personalised weekly, monthly, and yearly horoscopes. Access your Vedic astrology readings from Prakash Astrologer.',
        url: '/dashboard',
        noindex: true,
    },
    consultations: {
        title: 'Astrological Consultations',
        description: 'Book a personalised Vedic astrological consultation with Prakash Astrologer. Get expert guidance on career, relationships, health, and life decisions.',
        url: '/consultations',
    },
    consultationForm: {
        title: 'Book Consultation',
        description: 'Fill in your details to book a personalised Vedic astrology consultation with Prakash Astrologer.',
        url: '/consultation-form',
        noindex: true,
    },
    consultationConfirmation: {
        title: 'Consultation Confirmed',
        description: 'Your astrological consultation has been confirmed. View your booking details.',
        url: '/consultation-confirmation',
        noindex: true,
    },
    gemstones: {
        title: 'Astrological Gemstones & Jewellery',
        description: 'Discover authentic astrological gemstones recommended by Prakash Astrologer. Find the right gemstone based on your Vedic birth chart.',
        url: '/gemstones',
    },
    gemstoneEnquiry: {
        title: 'Gemstone Enquiry',
        description: 'Enquire about astrological gemstones. Get personalised gemstone recommendations from Prakash Astrologer.',
        url: '/gemstone-enquiry',
        noindex: true,
    },
    gemstoneEnquirySuccess: {
        title: 'Enquiry Submitted',
        description: 'Your gemstone enquiry has been submitted successfully. We will contact you shortly.',
        url: '/gemstone-enquiry-success',
        noindex: true,
    },
    moonsignLookup: {
        title: 'Find Your Moon Sign',
        description: 'Calculate your Vedic moon sign (Rashi) based on your birth details. Discover your lunar zodiac sign with Prakash Astrologer.',
        url: '/moonsign-lookup',
    },
    risingSignLookup: {
        title: 'Find Your Rising Sign (Ascendant)',
        description: 'Calculate your Vedic rising sign (Lagna/Ascendant) based on your birth details. Discover your ascendant with Prakash Astrologer.',
        url: '/rising-sign-lookup',
    },
    profile: {
        title: 'My Profile',
        description: 'Manage your Prakash Astrologer account settings and birth chart details.',
        url: '/profile',
        noindex: true,
    },
    mySubscriptions: {
        title: 'My Subscriptions',
        description: 'Manage your horoscope subscriptions and payment details.',
        url: '/my-subscriptions',
        noindex: true,
    },
    noticeboard: {
        title: 'Noticeboard',
        description: 'View the latest announcements and updates from Prakash Astrologer.',
        url: '/noticeboard',
        noindex: true,
    },
    lifeEventConfirmation: {
        title: 'Life Event Purchase Confirmed',
        description: 'Your life event purchase has been confirmed. Access your detailed yearly horoscope readings.',
        url: '/life-event-confirmation',
        noindex: true,
    },
    horoscopeGenerated: {
        title: 'Your Horoscope',
        description: 'Your personalised Vedic horoscope has been generated. View your astrological insights.',
        url: '/horoscope-generated',
        noindex: true,
    },
    paymentPlanSelect: {
        title: 'Select Payment Plan',
        description: 'Choose your horoscope subscription plan. Access premium Vedic astrology readings.',
        url: '/payment-plan-select',
        noindex: true,
    },
    enterPaymentDetails: {
        title: 'Enter Payment Details',
        description: 'Complete your purchase securely. Enter your payment information.',
        url: '/enter-payment-details',
        noindex: true,
    },
    orderConfirm: {
        title: 'Confirm Order',
        description: 'Review and confirm your order details.',
        url: '/order-confirm',
        noindex: true,
    },
    orderComplete: {
        title: 'Order Complete',
        description: 'Your order has been completed successfully. Thank you for your purchase.',
        url: '/order-complete',
        noindex: true,
    },
};

// Get Life Events SEO title
export const getLifeEventsSEOTitle = (signName) => `${signName} Life Events - Yearly Horoscope`;

// Get Life Events SEO description
export const getLifeEventsSEODescription = (signName) => `Explore detailed ${signName} yearly horoscope life events. Get in-depth Vedic astrology insights for career, love, health, and more from Prakash Astrologer.`;

// Get Life Events JSON-LD
export const getLifeEventsJsonLd = (signName, url) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${signName} Yearly Life Events Horoscope`,
    description: getLifeEventsSEODescription(signName),
    brand: {
        '@type': 'Organization',
        name: 'Prakash Astrologer',
    },
    offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'GBP',
    },
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
    },
});

// Get Gemstone SEO title
export const getGemstoneSEOTitle = (gemstoneName) => `${gemstoneName} - Astrological Gemstone`;

// Get Gemstone SEO description
export const getGemstoneSEODescription = (gemstoneName) => `Discover the astrological benefits of ${gemstoneName}. Learn about properties, wearing instructions, and planetary associations from Prakash Astrologer.`;

// Get Gemstone JSON-LD
export const getGemstoneJsonLd = (gemstoneName, gemstoneDescription, gemstoneImage, url) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: gemstoneName,
    description: gemstoneDescription,
    image: gemstoneImage,
    brand: {
        '@type': 'Organization',
        name: 'Prakash Astrologer',
    },
    mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
    },
});

// Sitemap URL configurations
export const SITEMAP_URLS = {
    static: [
        { url: '/', changefreq: 'weekly', priority: 1.0 },
        { url: '/signup', changefreq: 'monthly', priority: 0.8 },
        { url: '/consultations', changefreq: 'weekly', priority: 0.9 },
        { url: '/gemstones', changefreq: 'weekly', priority: 0.9 },
        { url: '/moonsign-lookup', changefreq: 'monthly', priority: 0.8 },
        { url: '/rising-sign-lookup', changefreq: 'monthly', priority: 0.8 },
    ],
    horoscopes: {
        types: ['weekly', 'monthly', 'yearly'],
        signs: ZODIAC_SIGNS,
    },
    lifeEvents: {
        signs: ZODIAC_SIGNS,
    },
};
